// ./src/components/features/Logbook/LogbookEditDrawer/sections/DateTimeVehicleSection.tsx

import React from "react";
import { NumberInput, Grid } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";

import { FormData } from "../../types";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useDashboardContext } from "@/contexts/DashboardContext";

import { CarSelector } from "@/components/features/Cars/CarSelector";

// ============================================
// Types
// ============================================

export interface DateTimeVehicleSectionProps {
  formData: FormData;
  onFieldChange: (field: string, value: string | number | boolean | string[] | null) => void;
  onDateChange: (value: string | null) => void;
  onOdometerChange: (value: number | string) => void;
  odometerUnit: string;
  apiFieldErrors: Record<string, string | undefined>;
}

// ============================================
// Component
// ============================================

const DateTimeVehicleSectionComponent: React.FC<DateTimeVehicleSectionProps> = ({
  formData,
  onFieldChange,
  onDateChange,
  onOdometerChange,
  odometerUnit,
  apiFieldErrors,
}) => {
  const { t } = useTranslation(["logbook", "common", "validation"]);
  const { totalCarsCount } = useDashboardContext();
  const { isMobile } = useAppEnvironment();

  return (
    <>
      {/* Date & Time */}
      <DateTimePicker
        label={t("logbook:form.fields.whenDone.label")}
        placeholder={t("logbook:form.fields.whenDone.placeholder")}
        value={formData.whenDone}
        onChange={onDateChange}
        error={apiFieldErrors.whenDone}
        maxDate={new Date()}
        size="md"
        required
        withAsterisk
        valueFormat="MMM DD, YYYY HH:mm"
        dropdownType={isMobile ? "modal" : "popover"}
        timePickerProps={{
          withDropdown: true,
          popoverProps: { withinPortal: true },
          format: "12h",
        }}
      />

      {/* Car & Odometer */}
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CarSelector
            label={t("logbook:form.fields.car.label")}
            name="carId"
            value={formData.carId}
            onChange={onFieldChange}
            error={apiFieldErrors.carId}
            disabled={totalCarsCount === 1}
            required
            size="md"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label={`${t("logbook:form.fields.odometer.label")} (${odometerUnit})`}
            placeholder={t("logbook:form.fields.odometer.placeholder")}
            value={formData.odometer ?? ""}
            onChange={onOdometerChange}
            error={apiFieldErrors.odometer}
            min={0}
            max={9999999}
            thousandSeparator=","
            size="md"
            hideControls
          />
        </Grid.Col>
      </Grid>
    </>
  );
};

export const DateTimeVehicleSection = React.memo(DateTimeVehicleSectionComponent);